<template>
  <page-loader :is-loading="false">
    <drawer-form-layout class="export-form">
      <template #header>Экспорт</template>
      <template #instruction>
        <template v-if="!!attachment">
          <p>
            Сохраните файл с данными.<br />
            После закрытия формы ссылка на файл будет недоступна.
          </p>
        </template>
        <template v-else>
          <p>
            Выберите формат данных для экспорта.<br />
            По умолчанию будут выгружены все проекты.<br />
            Для выгрузки всех Ваших проектов выберите "Все мои проекты".<br />
            Примените фильтр, чтобы выгрузить данные в соответствии с фильтром
            для активной вкладки.<br />
          </p>
        </template>
      </template>
      <template #default>
        <template v-if="!!attachment">
          <div class="attachment-block">
            <attachment-file :file="attachment" :is-editable="false" />
          </div>
        </template>
        <template v-else>
          <select-item
            v-model="exportType"
            name="exportTypeSelector"
            :options="
              fileTypes.map((type) => ({
                value: type.ext,
                label: type.description,
                disabled: type.disabled,
              }))
            "
            :disabled="!!attachment"
            placeholder="Выберите тип файла"
          />
          <a-checkbox
            v-model:checked="allMyProjects"
            :disabled="!!attachment"
            @change="onAllMyProjectsChange"
          >
            Все мои проекты
          </a-checkbox>
          <a-checkbox
            v-model:checked="useFilter"
            :disabled="!!attachment"
            @change="onUseFilterChange"
          >
            Применить фильтр
          </a-checkbox>
          <a-checkbox v-model:checked="copyToEmail" :disabled="!!attachment">
            Отправить копию на e-mail
          </a-checkbox>
        </template>
      </template>
      <template #controls>
        <right-panel-buttons>
          <template v-if="!!attachment">
            <button-default @click="closeDrawer"> Закрыть </button-default>
          </template>
          <template v-else>
            <button-default @click="closeDrawer"> Отмена </button-default>
            <c-button-primary :loading="isLoading" @click="getExportData">
              Выгрузить данные
            </c-button-primary>
          </template>
        </right-panel-buttons>
      </template>
    </drawer-form-layout>
  </page-loader>
</template>

<script setup>
  import ButtonDefault from '@/views/portal/components/UI/ButtonDefault.vue';
  import RightPanelButtons from '@/views/portal/components/panels/RightPanelButtons.vue';
  import SelectItem from '@/components/form/components/SelectItem.vue';
  import { EXPORT_FILE_TYPE_XLSX } from '@/constants/export-file-type';
  import { ref } from 'vue';
  import DrawerFormLayout from '@/components/form/layouts/DrawerFormLayout.vue';
  import PageLoader from '@/components/loaders/PageLoader.vue';
  import { useApi } from '@/api/use-api';
  import CButtonPrimary from '@/components/UI/CButtonPrimary.vue';
  import AttachmentFile from '@/components/attachment-file/AttachmentFile.vue';
  import { ApiAttachmentFileAdapter } from '@/adapters/api-attachment-file';
  import {
    PROJECT_TAB_ALL,
    PROJECT_TAB_IN_WORK,
  } from '@/constants/project-tab';
  import { useRoute } from 'vue-router';

  const props = defineProps({
    searchParams: Object,
  });

  const api = useApi();
  const route = useRoute();
  const emit = defineEmits(['close']);
  const exportType = ref(EXPORT_FILE_TYPE_XLSX);
  const allMyProjects = ref(true);
  const useFilter = ref(false);
  const copyToEmail = ref(false);
  const attachment = ref(null);
  const isLoading = ref(false);

  const fileTypes = [
    // {
    //   ext: EXPORT_FILE_TYPE_CSV,
    //   description: 'Экспорт в формате CSV',
    //   disabled: true,
    // },
    {
      ext: EXPORT_FILE_TYPE_XLSX,
      description: 'Экспорт в формате XLSX',
      disabled: false,
    },
  ];

  const onAllMyProjectsChange = () => {
    if (useFilter.value) {
      useFilter.value = false;
    }
  };

  const onUseFilterChange = () => {
    if (useFilter.value) {
      allMyProjects.value = false;
    }
  };

  const closeDrawer = () => {
    emit('close');
  };

  const getExportData = async () => {
    isLoading.value = true;
    try {
      const searchParams = new URLSearchParams(props.searchParams);

      if (allMyProjects.value) {
        searchParams.set('tab', PROJECT_TAB_ALL);
      }

      if (useFilter.value) {
        const tab = route.hash
          ? route.hash.replace('#', '')
          : PROJECT_TAB_IN_WORK;
        searchParams.set('tab', tab);
      }

      const response = await api.project.export({
        sendEmail: copyToEmail.value,
        useFilter: allMyProjects.value || useFilter.value,
        searchParams: searchParams
          ? Object.fromEntries(searchParams.entries())
          : null,
      });
      attachment.value = ApiAttachmentFileAdapter.toFile(response.attachment);
    } catch (error) {
      console.error('error', error);
    } finally {
      isLoading.value = false;
    }
  };
</script>

<style scoped lang="scss">
  .checkbox-item {
    margin-bottom: 0;
  }
</style>
